import { useEffect, useState } from "react";
import { ResponsiveLine, Serie, SliceTooltipProps } from "@nivo/line";

import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";

import { SensorReadings } from "../../api/interfaces";
import { Period } from "../../api/types/sensor";
import classes from "./index.module.scss";

type Props = {
  title?: string;
  data: SensorReadings[];
  isLoading: boolean;
  sensorType: string;
  containerHeight: string;
  simple?: boolean;
  period: Period;
};

const LineChart = (props: Props) => {
  const {
    title,
    data,
    sensorType,
    isLoading,
    containerHeight,
    simple,
    period,
  } = props;
  const [chartData, setChartData] = useState<Serie[]>([]);

  useEffect(() => {
    const formatData = () => {
      const newChartData: Serie[] = [
        {
          id: sensorType,
          data: [],
        },
      ];

      for (let dataPoint of data) {
        newChartData[0].data.push({
          x: new Date(dataPoint.period).toLocaleString(),
          y: dataPoint.reading,
        });
      }

      setChartData(newChartData);
    };

    formatData();
  }, [data, sensorType]);

  const getMaxValue = () => {
    return Math.max(...data.map((el) => el.reading));
  };

  /**
   * Renders a custom tooltip containing the
   * date and sensor reading
   */
  const renderTooltip = ({ slice }: SliceTooltipProps) => {
    const point =
      slice.points.filter((p) => p.data.yFormatted !== 0)[0] || slice.points[1];

    const date =
      period === "day"
        ? new Date(point.data.xFormatted).toLocaleString()
        : new Date(point.data.xFormatted).toDateString();

    return (
      <div className={classes.tooltipContainer}>
        <strong className={classes.tooltipTitle}>{date}</strong>
        <div className={classes.tooltipBody}>
          Sensor reading:
          <div style={{ padding: "0px 5px" }}>
            <strong>{point.data.yFormatted}</strong>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Format sensor reading to be 3 digits with
   * leading 0s. E.g. 002 or 014
   */
  const renderLeftAxis = (v: number) => {
    const wholeNumberLabel = Math.trunc(v) || "000";
    const stringLabel = wholeNumberLabel.toString();

    if (sensorType === "pm25") {
      // Format Labels to have the 000 value format
      if (stringLabel.length === 2) return `0${stringLabel}`;
      else if (stringLabel.length === 1) return `00${stringLabel}`;
      return stringLabel;
    } else return stringLabel;
  };

  return (
    <Card
      style={{
        width: "100%",
        height: containerHeight,
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      }}
      className={classes.container}
    >
      {title && (
        <p style={{ fontSize: simple ? 16 : 24 }} className={classes.cardTitle}>
          {title}
        </p>
      )}
      {isLoading ? (
        <div className={classes.loaderContainer}>
          <CircularProgress style={{ alignSelf: "center", margin: "auto" }} />
          <div className={classes.loaderText}>Loading the latest data. Please be patient</div>
        </div>
      ) : data.length > 0 ? (
        <ResponsiveLine
          data={chartData}
          curve="monotoneY"
          margin={{ top: 0, bottom: 50, left: 60, right: 50 }}
          lineWidth={1}
          colors={["rgb(255,255,255)"]}
          layers={["axes", "areas", "crosshair", "lines", "slices"]}
          theme={{
            textColor: "rgba(255, 255, 255, 0.514)",
            crosshair: {
              line: {
                stroke: "#fff",
                strokeWidth: 1,
                strokeOpacity: 0.75,
                strokeDasharray: "6 6",
              },
            },
          }}
          xScale={{
            type: "time",
            format: "%d/%m/%Y, %H:%M:%S",
            useUTC: false,
            precision: "hour",
          }}
          yScale={{ type: "linear", min: 0, max: getMaxValue() + 10 }}
          axisBottom={{
            format: period === "day" ? "%b %d %Y %H:%M" : "%b %d %Y",
            tickValues: simple ? 2 : 3,
            tickSize: 6,
            tickRotation: simple? 20 : 0
          }}
          axisLeft={{
            format: renderLeftAxis,
            legend: sensorType,
            legendOffset: -40,
            legendPosition: "middle",
            tickValues: simple ? 2 : 3,
          }}
          motionConfig="gentle"
          enableArea
          areaBaselineValue={0}
          areaOpacity={0.3}
          enablePoints={false}
          enableSlices="x"
          sliceTooltip={renderTooltip}
        />
      ) : (
        <div className={classes.noDataBody}>No data for this period</div>
      )}
    </Card>
  );
};

export default LineChart;
